import React, { useState, useContext, useEffect } from "react";
import { Collapse } from "react-bootstrap";
import { useTranslation } from "react-i18next";

import { TriangleUpIcon, ShieldIcon } from "@components/icons";
import { styles } from "@components/styles";
import { constants } from "../context/constants";
import { DataScienceContext } from "../context/DataScienceContext";
import {
  VARIABLE_DEFINITIONS,
  getVariableById,
  getVariableDisplayLabel,
} from "../utils/variableMetadata";

function VariableList() {
  const ctx = useContext(DataScienceContext);
  if (!ctx) throw new Error("DataScienceContext missing");

  const {
    filterState,
    filterDispatch,
    activeInstallations,
    setDragItemLeft,
    setDragItemRight,
    dragItemLeft,
    dragItemRight,
    dragItemLeftId,
    dragItemRightId,
    setDragItemLeftId,
    setDragItemRightId,
    setPositionLeft,
    setPositionRight,
    positionLeft,
    positionRight,
    showMoistureThresholds,
    setShowMoistureThresholds,
    hasMoistureThresholdContext,
  } = ctx;
  const { variablesId, installationsId, installations } = filterState;
  const [open, setOpen] = useState(true);
  const { t } = useTranslation();
  const [possibleVariables, setPossibleVariables] = useState([]);
  const selectedInstallationsResolvedCount = installationsId.filter((selectedId) =>
    installations.some((installation) => installation.id === selectedId)
  ).length;
  const activeInstallationsResolvedCount = installationsId.filter((selectedId) =>
    activeInstallations.some((installation) => installation.id === selectedId)
  ).length;
  const waitingForInstallationsHydration =
    installationsId.length > 0 &&
    (selectedInstallationsResolvedCount < installationsId.length ||
      activeInstallationsResolvedCount < installationsId.length);

  useEffect(() => {
    if (waitingForInstallationsHydration) return;

    const hasSensorType = (type: string) =>
      !activeInstallations.every((inst) => inst.sensor[type] == null);

    // Check if any installation has van-Genuchten parameters
    const hasVanGenuchtenParams = activeInstallations.some(
      (inst) => inst.has_van_genuchten_params === true
    );

    const sensorTypeActive: Record<string, boolean> = {
      pulse: hasSensorType("pulse_sensor"),
      soil_moisture: hasSensorType("soil_moisture_sensor"),
      soil_water_tension: hasSensorType("soil_water_tension_sensor"),
      dendrometer: hasSensorType("dendrometer_sensor"),
    };

    // Filter variables: exclude VG variables if no installation has VG params
    const vars = VARIABLE_DEFINITIONS.filter((v) => {
      // First check if sensor type is active
      if (!sensorTypeActive[v.sensorType]) return false;
      // For VG variables, also check if any installation has VG params
      if (v.field === "soil_water_tension_vg" || v.field === "soil_moisture_vg") {
        return hasVanGenuchtenParams;
      }
      return true;
    }).map((v) => ({
      id: v.id,
      label: v.field,
    }));

    setPossibleVariables(vars);
    filterDispatch({
      type: constants.SET_VARIABLES,
      payload: vars,
    });
  }, [activeInstallations, waitingForInstallationsHydration, filterDispatch]);

  useEffect(() => {
    if (waitingForInstallationsHydration) return;
    // Avoid clearing URL-hydrated variable selections while sensors are still resolving.
    if (installationsId.length > 0 && possibleVariables.length === 0) return;
    const allowedIds = new Set(possibleVariables.map((v) => v.id));

    if (dragItemLeftId != null && !allowedIds.has(dragItemLeftId)) {
      setDragItemLeftId(null);
      setDragItemLeft(null);
      setPositionLeft(false);
    }
    if (dragItemRightId != null && !allowedIds.has(dragItemRightId)) {
      setDragItemRightId(null);
      setDragItemRight(null);
      setPositionRight(false);
    }

    const filteredVariables = variablesId.filter((id) => allowedIds.has(id));
    const changed =
      filteredVariables.length !== variablesId.length ||
      filteredVariables.some((id) => !variablesId.includes(id));

    if (changed) {
      filterDispatch({ type: constants.RESET_CHECKED_VARIABLES, payload: filteredVariables });
    }
  }, [
    possibleVariables,
    dragItemLeftId,
    dragItemRightId,
    variablesId,
    setDragItemLeft,
    setDragItemRight,
    setDragItemLeftId,
    setDragItemRightId,
    setPositionLeft,
    setPositionRight,
    filterDispatch,
    waitingForInstallationsHydration,
  ]);

  useEffect(() => {
    if (dragItemLeft && dragItemLeftId == null && possibleVariables.length > 0) {
      const match = possibleVariables.find((v) => v.label === dragItemLeft);
      if (match) setDragItemLeftId(match.id);
    }
    if (dragItemRight && dragItemRightId == null && possibleVariables.length > 0) {
      const match = possibleVariables.find((v) => v.label === dragItemRight);
      if (match) setDragItemRightId(match.id);
    }
  }, [
    dragItemLeft,
    dragItemRight,
    dragItemLeftId,
    dragItemRightId,
    possibleVariables,
    setDragItemLeftId,
    setDragItemRightId,
  ]);

  const handleChange = (props) => {
    const { e, variable } = props;
    if (e.target.checked) {
      if (positionRight) {
        filterDispatch({ type: constants.RESET_CHECKED_VARIABLES, payload: [variable.id] });
        setDragItemLeft(variable.label);
        setDragItemLeftId(variable.id);
        setDragItemRight(null);
        setDragItemRightId(null);
        setPositionRight(false);
      } else if (positionLeft) {
        filterDispatch({ type: constants.SET_CHECKED_VARIABLES, payload: [variable.id] });
        setDragItemRight(variable.label);
        setDragItemRightId(variable.id);
        setPositionRight(true);
      } else {
        filterDispatch({ type: constants.SET_CHECKED_VARIABLES, payload: [variable.id] });
        setDragItemLeft(variable.label);
        setDragItemLeftId(variable.id);
        setPositionLeft(true);
      }
    }
    if (e.target.checked === false) {
      const filteredVariables = variablesId.filter((id) => id !== variable.id);
      if (dragItemLeftId === variable.id) {
        setDragItemLeft(null);
        setDragItemLeftId(null);
        setPositionLeft(false);
      } else {
        setDragItemRight(null);
        setDragItemRightId(null);
        setPositionRight(false);
      }
      filterDispatch({ type: constants.RESET_CHECKED_VARIABLES, payload: filteredVariables });
    }
  };

  return (
    <div className="me-3" data-tour="ds-filter-variables">
      <div className="d-flex pe-2 justify-content-between">
        <div
          className={styles.dsFilterTitle}
          onClick={() => setOpen(!open)}
          aria-controls="example-collapse-text"
          aria-expanded={open}
        >
          <TriangleUpIcon open={open} /> <span className=" ds-filter-title">{t("variables")}</span>
        </div>
      </div>
      <Collapse in={open}>
        <div>
          <ul className="p-0 pe-2 filter__list" id="example-collapse-text">
            {possibleVariables?.map((variable) => {
              const def = getVariableById(variable.id);
              const displayLabel = def ? getVariableDisplayLabel(def, t) : t(variable.label);
              return (
                <li
                  key={variable.id}
                  className={`filter__items  gap-1 d-flex justify-content-between ps-1`}
                >
                  <div className="d-flex align-items-center gap-2">
                    <span>{displayLabel}</span>
                  </div>
                  <div className="d-flex align-items-center gap-2">
                    {["moisture_content", "soil_moisture", "soil_water_tension"].includes(
                      variable.label
                    ) && (
                      <button
                        type="button"
                        className="btn btn-sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowMoistureThresholds(!showMoistureThresholds);
                        }}
                        title={t("thresholds")}
                        style={{
                          fontSize: "12px",
                          padding: "2px 8px",
                          minWidth: "auto",
                          border: "none",
                          backgroundColor:
                            hasMoistureThresholdContext && showMoistureThresholds
                              ? "#a8a9aa"
                              : "transparent",
                          color: hasMoistureThresholdContext ? "#0d6efd" : "#adb5bd",
                          opacity: hasMoistureThresholdContext ? 1 : 0.7,
                          cursor: "pointer",
                        }}
                      >
                        <ShieldIcon size="14px" />
                      </button>
                    )}
                    <input
                      type="checkbox"
                      onChange={(e) => {
                        handleChange({ e, variable });
                      }}
                      checked={variablesId.includes(variable.id)}
                    />
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      </Collapse>
    </div>
  );
}

export default React.memo(VariableList);
